import { DidomiSDK, IDidomiObject } from '@didomi/react';
import { DIDOMI_AMPLITUDE_CONSENT_ID, DIDOMI_API_KEY, DIDOMI_BRAZE_SDK_ID, DIDOMI_CONSENT_ID } from '@env';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import amplitude from 'src/_shared/amplitude';
import Datadog from 'src/_shared/datadog';
import { DATADOG_CUSTOM_ACTIONS } from 'src/_shared/datadog/DatadogCustomActions';
import { BOOTSTRAP_CONTEXT, captureConnectionContext } from 'src/_shared/datadog/bootstrapContext';
import { logInfo, logWarning } from 'src/_shared/logger';
import braze from 'src/_shared/braze';
import { reloadPage } from 'src/_shared/utils/reload-page/reloadPage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IRootStore } from 'src/store/store';
import { useGtmService } from 'src/editor-v3/hooks/useGtmService';

type DidomiProps = {
  handleDidomiReady: () => void;
};

// If an ad/content blocker prevents the Didomi CDN script from loading, onReady never fires.
// Without a fallback, isDidomiReady stays false forever and the app is stuck on the loading screen.
const DIDOMI_READY_TIMEOUT_MS = 5000;

export const Didomi = observer(({ handleDidomiReady }: DidomiProps) => {
  const store = useStore();
  const [didomiObject, setDidomiObject] = useState<IDidomiObject | null>(null);
  const isInitialized = useRef<boolean>(false);
  const isReadyHandled = useRef<boolean>(false);
  useGtmService();

  const handleReadyOnce = useCallback(() => {
    if (isReadyHandled.current) return;
    isReadyHandled.current = true;
    handleDidomiReady();
  }, [handleDidomiReady]);

  const onReady = useCallback((didomi: IDidomiObject) => {
    // Pure Didomi consent SDK ready: script CDN caricato + cookie/IAB TCF parsati.
    // Non include amplitude.init / braze.init che vengono fatti subito dopo in initializeSDKs.
    Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_didomi_consent_ready, {
      ...BOOTSTRAP_CONTEXT,
      ...captureConnectionContext(),
    });
    setDidomiObject(didomi);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isReadyHandled.current) {
        logWarning(
          '[Didomi] SDK not ready after',
          DIDOMI_READY_TIMEOUT_MS,
          'ms (likely blocked by a content/ad blocker), continuing without consent-gated SDKs',
        );
        Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_didomi_consent_ready);
        handleReadyOnce();
      }
    }, DIDOMI_READY_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [handleReadyOnce]);

  const initializeSDKs = useCallback(async (didomiObject: IDidomiObject, store: IRootStore) => {
    logInfo('[Didomi] Init SDKs');
    /* SDKs that require user consents to be initialized */
    const canBeInitialized = amplitude.canBeInitialized();
    const userConsents = didomiObject.getUserConsentStatusForAll().vendors.enabled as any[];
    const isAmplitudeAccepted = userConsents.includes(DIDOMI_AMPLITUDE_CONSENT_ID);
    const isBrazeAccepted = userConsents.includes(DIDOMI_BRAZE_SDK_ID);

    if (isAmplitudeAccepted && canBeInitialized) {
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_amplitude_init_time);
      await amplitude.init(store);
      Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_amplitude_init_time, {
        ...BOOTSTRAP_CONTEXT,
        ...captureConnectionContext(),
      });
    } else {
      // amplitude is not initialized here so will skip syncSDKFlagVariants
      amplitude.setAmplitudeStore(store);
    }

    if (isBrazeAccepted) {
      braze.init();
    }

    logInfo('[Didomi] COMPLETED init SDKs', {
      canBeInitialized,
      isAmplitudeAccepted,
      isBrazeAccepted,
    });
    handleReadyOnce();
  }, []);

  useEffect(() => {
    if (!!didomiObject && !isInitialized.current) {
      isInitialized.current = true;
      // could be the initialStore, but it's ok because syncSDKFlagVariants is idempotent, and will be re-run when the real store is ready
      initializeSDKs(didomiObject, store);
    }
  }, [didomiObject, initializeSDKs, store]);

  useEffect(() => {
    if (store.isStoreReady) {
      // store is now the real store, with SDK ready, so we can set it in amplitude
      amplitude.setAmplitudeStore(store);
    }
  }, [store]);

  return (
    <DidomiSDK
      apiKey={DIDOMI_API_KEY}
      noticeId={DIDOMI_CONSENT_ID}
      iabVersion={2}
      embedTCFStub={true}
      gdprAppliesGlobally={true}
      onReady={onReady}
      onConsentChanged={cwtToken => {
        reloadPage();
      }}
    />
  );
});

export default Didomi;
